import React, { useMemo, useState } from "react";

import { DeleteOutline as DeleteOutlineIcon } from "@mui/icons-material";
import {
    Backdrop,
    Box,
    CircularProgress,
    Divider,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";

import TrackerSettings from "pages/Trackers/TrackerSettings";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { createDomainsAsync } from "helpers/trackers/createDomains";
import { deleteDomainsAsyns } from "helpers/trackers/deleteDomains";

import StandartButton from "components/atoms/StandartButton";
import { ConfirmationModal } from "components/organisms/ConfirmationModal";
import SwalAlertComp from "components/SwalAlert";

import { getSingleTrackerRedux } from "../../../../../../../redux/slices/trackers/trackers.asyncActions";
import { hasFeature } from "../../../SourceModal.config";
import { TabProps } from "../../../SourceModal.types";

interface Domain {
    id: number;
    domain: string;
}

interface SettingsTabProps extends TabProps {
    allowedDomains?: Domain[];
}

const SettingsTab: React.FC<SettingsTabProps> = ({
    source,
    tracker,
    language,
    onRefresh,
}) => {
    const dispatch = useDispatch();
    const params = useParams<{ id: string }>();

    const trackerStatus = useSelector(
        (state: any) => state.allTrackersState.singleTrackerStatus,
    );

    const [domainInput, setDomainInput] = useState("");
    const [isAddingDomain, setIsAddingDomain] = useState(false);
    const [isDeletingDomain, setIsDeletingDomain] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDomain, setPendingDomain] = useState<Domain | null>(null);
    const [deletingDomainId, setDeletingDomainId] = useState<number | null>(
        null,
    );

    const sourceCode = source?.source_type?.code;
    const showDomains = hasFeature(sourceCode, "domains");
    const isRedirect = sourceCode === "redirect";

    const allowedDomains = useMemo(() => {
        return (
            tracker?.tracker?.sources?.find(
                (item: any) =>
                    (item.source_type.code === "js" ||
                        item.source_type.code === "redirect") &&
                    item.id === source?.id,
            )?.allowed_domains || source?.allowed_domains
        );
    }, [tracker]);

    const handleAddDomain = async () => {
        if (!domainInput.trim()) return;

        setIsAddingDomain(true);
        try {
            const sourceId =
                tracker?.tracker?.sources?.find(
                    (item: any) =>
                        (item.source_type.code === "js" ||
                            item.source_type.code === "redirect") &&
                        item.id,
                )?.id || source?.id;

            const domains = {
                domain: domainInput,
                source_id: sourceId,
            };

            const response = await (createDomainsAsync as any)(domains);
            if (response.status) {
                setDomainInput("");
                if (params.id) {
                    dispatch(getSingleTrackerRedux(params.id));
                }
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message,
                    icon: "error",
                });
            }
        } catch (error) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: "An error occurred while adding the domain",
                icon: "error",
            });
        } finally {
            setIsAddingDomain(false);
        }
    };

    const handleDeleteDomain = async (domainId: number) => {
        setIsDeletingDomain(true);
        setDeletingDomainId(domainId);
        try {
            const response = await (deleteDomainsAsyns as any)(domainId);
            if (response.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response.message,
                    icon: "success",
                    timer: 3000,
                });
                if (params.id) {
                    dispatch(getSingleTrackerRedux(params.id));
                }
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message,
                    icon: "error",
                });
            }
        } catch (error) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: "An error occurred while deleting the domain",
                icon: "error",
            });
        } finally {
            setIsDeletingDomain(false);
            setDeletingDomainId(null);
        }
    };

    const confirmDeleteDomain = (domain: Domain) => {
        setPendingDomain(domain);
        setConfirmOpen(true);
    };

    return (
        <Box>
            <ConfirmationModal
                open={confirmOpen}
                onClose={() => {
                    setConfirmOpen(false);
                    setPendingDomain(null);
                }}
                onConfirm={() => {
                    if (pendingDomain) handleDeleteDomain(pendingDomain.id);
                    setConfirmOpen(false);
                    setPendingDomain(null);
                }}
                title={
                    language?.delete_swal_modal?.are_you_sure ?? "Are you sure?"
                }
                message="This action cannot be undone."
                confirmColor="error"
            />

            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                }}
                open={trackerStatus === "loading"}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            {showDomains && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {language?.tracker_detail?.signal_host_name ??
                            "Signal Host Name"}
                        :{" "}
                        <Typography component="span" sx={{ fontWeight: 600 }}>
                            {tracker?.tracker?.source?.source_types?.[0]
                                ?.host || "Used generic domain"}
                        </Typography>
                    </Typography>
                </Box>
            )}

            {showDomains && (
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 2,
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {isRedirect
                                ? (language?.buttons?.domains ?? "Domains")
                                : (language?.buttons?.allowed_domains ??
                                  "Allowed Domains")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {allowedDomains?.length || 0}{" "}
                            {allowedDomains?.length === 1
                                ? "domain"
                                : "domains"}
                        </Typography>
                    </Box>

                    {allowedDomains?.length > 0 ? (
                        <Box
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                overflow: "hidden",
                                backgroundColor: "background.paper",
                            }}
                        >
                            {allowedDomains.map((domain, index) => (
                                <Box
                                    key={domain.id}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        p: 2,
                                        backgroundColor:
                                            index % 2 === 0
                                                ? "grey.50"
                                                : "background.paper",
                                        borderBottom:
                                            index < allowedDomains.length - 1
                                                ? "1px solid"
                                                : "none",
                                        borderBottomColor: "divider",
                                        transition: "all 0.2s ease-in-out",
                                        "&:hover": {
                                            backgroundColor: "action.hover",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            flex: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                backgroundColor: "success.main",
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                fontFamily: "monospace",
                                                fontSize: "0.875rem",
                                            }}
                                        >
                                            {domain.domain}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={
                                            isDeletingDomain &&
                                            deletingDomainId === domain.id
                                        }
                                        onClick={() =>
                                            confirmDeleteDomain(domain)
                                        }
                                    >
                                        {isDeletingDomain &&
                                        deletingDomainId === domain.id ? (
                                            <CircularProgress
                                                size={16}
                                                color="inherit"
                                            />
                                        ) : (
                                            <DeleteOutlineIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                border: "2px dashed",
                                borderColor: "divider",
                                borderRadius: 2,
                                p: 4,
                                textAlign: "center",
                                backgroundColor: "grey.50",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                No domains added yet. Add your first domain
                                below.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {showDomains && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Add New Domain
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "center",
                        }}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            value={domainInput}
                            onChange={(e) => setDomainInput(e.target.value)}
                            disabled={isAddingDomain}
                            placeholder={
                                isRedirect
                                    ? "https://www.example.com"
                                    : "example.com"
                            }
                            onKeyPress={(e) =>
                                e.key === "Enter" &&
                                !isAddingDomain &&
                                handleAddDomain()
                            }
                        />
                        <StandartButton
                            variant="contained"
                            color="primary"
                            onClick={handleAddDomain}
                            disabled={!domainInput.trim() || isAddingDomain}
                            isLoading={isAddingDomain}
                            wrapperSx={{ height: 40 }}
                            sx={{
                                minWidth: 120,
                                height: 40,
                                textTransform: "none",
                                fontWeight: 600,
                            }}
                        >
                            {isRedirect
                                ? (language?.buttons?.add_domain ??
                                  "Add Domain")
                                : (language?.buttons?.add_allowed_domain ??
                                  "Add Domain")}
                        </StandartButton>
                    </Box>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                    >
                        {isRedirect
                            ? "Enter the full URL of your website"
                            : "Enter the domain name (e.g., example.com)"}
                    </Typography>
                </Box>
            )}

            {showDomains && <Divider sx={{ my: 2 }} />}

            {tracker && tracker?.tracker?.id && (
                <TrackerSettings
                    id={tracker?.tracker?.id}
                    language={language}
                />
            )}
        </Box>
    );
};

export default SettingsTab;
